import { createFileRoute } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/sitemap-zero-urls-error";
const TITLE =
  "\"0 URLs, 1 error\" in Search Console: when it's a real problem and when it isn't";
const DESCRIPTION =
  "Two of the most-Googled Search Console sitemap errors — empty news sitemap and empty published-articles sitemap — explained in plain English. Includes the rule for when to ignore vs fix them.";

export const Route = createFileRoute("/learn/sitemap-zero-urls-error")({
  head: () =>
    learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="SEO"
      title={TITLE}
      intro="If you opened Google Search Console, looked at your sitemap report, and saw a row that says '0 URLs' with a red '1 error' next to it, you're in the right place. Here's exactly what's going on, when it matters, and when it doesn't."
      readTime="5 min read"
      publishedDate="2026-05-02"
      path={PATH}
      definition={{
        term: "What does \"0 URLs, 1 error\" mean in Google Search Console?",
        body: "It almost always means Google fetched your sitemap successfully, but the sitemap was empty — there were zero <url> entries inside it. Google flags any empty sitemap as an error, even though an empty sitemap is technically valid XML. It's not a penalty, it's a notice. The fix depends on which sitemap is empty and why.",
      }}
      toc={[
        { id: "what-it-means", label: "What \"0 URLs, 1 error\" actually means" },
        { id: "case-news", label: "Case 1: News sitemap is empty" },
        { id: "case-published", label: "Case 2: DB-backed sitemap is empty" },
        { id: "rule-of-thumb", label: "The rule of thumb" },
        { id: "how-to-clean-up", label: "How to clean it up in Search Console" },
        { id: "what-to-ignore", label: "What you can safely ignore" },
      ]}
      faqs={[
        {
          q: "Will Google penalize my site for an empty sitemap?",
          a: "No. Google does not penalize sites for empty sitemaps. The 'error' label in Search Console is a status indicator, not a ranking factor. Your other sitemaps and pages are crawled and ranked completely independently of this one row.",
        },
        {
          q: "Should I delete the empty sitemap file?",
          a: "Only if you don't plan to populate it. If it's a news sitemap and you don't publish news daily, yes — remove it from your sitemap index. If it's a published-articles sitemap and you'll be publishing soon, leave it. The error will resolve on its own as soon as the first URL appears.",
        },
        {
          q: "What about /image-sitemap.xml or /video-sitemap.xml showing the same error?",
          a: "Same logic. An empty image sitemap means no <image:image> entries were emitted. Either remove it from the sitemap index until you have media to list, or wait for the next build cycle to populate it. Neither version harms your rankings.",
        },
        {
          q: "How is this different from a real sitemap error?",
          a: "A 'real' sitemap error is malformed XML, an HTTP 500 response, an unreachable URL, or URLs that violate your robots.txt. Those should be fixed immediately because they block Google from reading the sitemap at all. '0 URLs, 1 error' means Google read the file fine — it was just empty. Big difference.",
        },
        {
          q: "Can I just leave it and forget about it?",
          a: "Yes, in most cases. The empty-sitemap error doesn't compound, doesn't escalate, and doesn't bleed into other Search Console reports. The only practical reason to clean it up is to keep your sitemap report visually green so real errors stand out the next time one shows up.",
        },
      ]}
    >
      <h2 id="what-it-means">What "0 URLs, 1 error" actually means</h2>
      <p>
        A sitemap is a list of URLs you're asking Google to crawl. The format
        is XML — a <code>&lt;urlset&gt;</code> tag wrapping one
        <code>&lt;url&gt;</code> entry per page. When Google fetches the file,
        it counts the entries inside.
      </p>
      <p>
        If the count is zero — meaning the file exists, the XML is valid, but
        there are no <code>&lt;url&gt;</code> entries — Google logs it as an
        error in the Sitemaps report. The wording is misleading because nothing
        is actually broken. The file works. It's just empty right now.
      </p>
      <p>
        This shows up in two specific situations on a healthy site, and both
        are usually harmless.
      </p>

      <h2 id="case-news">Case 1: News sitemap is empty</h2>
      <p>
        Google News sitemaps follow a different rule than regular sitemaps:
        they're only allowed to contain articles published within the last
        <strong>48 hours</strong>. If you publish a new blog post on Monday,
        it's eligible to appear in your news sitemap until Wednesday morning,
        then it falls off automatically.
      </p>
      <p>
        For most Colorado businesses — HVAC companies, law firms, dentists,
        roofers — this means the news sitemap is empty almost all the time.
        You're not publishing daily, so there's nothing recent enough to
        include. The "1 error" is permanent until your publishing cadence
        changes.
      </p>
      <p>
        <strong>The honest fix:</strong> if you don't publish at least 2–3
        new articles per week, remove the news sitemap from your sitemap index
        entirely. Google News indexing is a niche feature for actual news
        publishers (newspapers, magazines, daily blogs). For a typical local
        service business, your blog content gets indexed perfectly well via
        your regular sitemap — no news sitemap required.
      </p>

      <h2 id="case-published">Case 2: DB-backed / published-articles sitemap is empty</h2>
      <p>
        Many modern websites separate their content into two sitemap groups:
        one for the static pages built into the site (services, locations,
        about, contact) and one for content stored in a database — usually AI-
        or editorially-published articles that get added over time.
      </p>
      <p>
        When the database table is empty (no published articles yet), the
        sitemap is empty too. Same "0 URLs, 1 error" outcome. The fix is
        nothing — the sitemap will populate itself the moment the first
        article publishes, and the error will clear on Google's next crawl
        (usually within a few days).
      </p>
      <p>
        If you've had the empty published-articles sitemap submitted for
        weeks and still haven't shipped any content, that's a content
        problem, not a sitemap problem. The sitemap is correctly reporting
        that you have nothing to show.
      </p>

      <h2 id="rule-of-thumb">The rule of thumb</h2>
      <p>
        Use this simple decision tree when you see "0 URLs, 1 error" in
        Search Console:
      </p>
      <ul>
        <li>
          <strong>Will this sitemap have content soon?</strong> Leave it.
          The error resolves automatically when the first URL appears.
        </li>
        <li>
          <strong>Will it always be empty?</strong> Remove it from your
          sitemap index and unsubmit it from Search Console.
        </li>
        <li>
          <strong>Was it accidentally created by a plugin or template?</strong>
          Delete the file from the server and unsubmit it.
        </li>
      </ul>
      <p>
        The mistake to avoid: panicking and deleting your entire sitemap
        infrastructure because one row is red. The other 6 or 7 sitemap rows
        next to it are likely showing "Success" with hundreds of URLs each —
        those are doing exactly what they should.
      </p>

      <h2 id="how-to-clean-up">How to clean it up in Search Console</h2>
      <p>
        If you decide to remove an empty sitemap, the cleanup is two steps:
      </p>
      <ol>
        <li>
          <strong>Remove it from your sitemap index file.</strong> Most sites
          have a master <code>sitemap.xml</code> that points to sub-sitemaps
          like <code>sitemap-cities.xml</code> and <code>sitemap-news.xml</code>.
          Edit the index to remove the line that references the empty one.
        </li>
        <li>
          <strong>Unsubmit it in Search Console.</strong> Go to
          <em>Sitemaps</em> → click the row → click the three-dot menu →
          <em>Remove sitemap</em>. Within a few days the row disappears from
          your report.
        </li>
      </ol>
      <p>
        If you want to keep the file but have it report "Success / 0 URLs"
        instead of "1 error," configure it to return a valid empty
        <code>&lt;urlset&gt;</code> document (the wrapper tag with no
        children). Some Google Search Console configurations treat that as
        success rather than error.
      </p>

      <h2 id="what-to-ignore">What you can safely ignore — and what you can't</h2>
      <p>
        Here's the full picture of which Search Console sitemap states matter
        and which don't:
      </p>
      <ul>
        <li>
          <strong>Success, N URLs:</strong> Working as intended. Nothing to do.
        </li>
        <li>
          <strong>Success, 0 URLs (or "1 error" with 0 URLs):</strong>
          Empty sitemap. Almost always harmless. Apply the rule of thumb
          above.
        </li>
        <li>
          <strong>Couldn't fetch:</strong> Real problem. Your sitemap URL is
          returning a 404 or 500. Fix the route immediately — Google can't
          read it at all.
        </li>
        <li>
          <strong>Has errors:</strong> Real problem. Click into the report
          to see which URLs are broken or which lines are malformed XML.
        </li>
        <li>
          <strong>Invalid sitemap:</strong> Real problem. The XML is
          malformed. Validate it with a sitemap validator and fix the
          syntax.
        </li>
      </ul>
      <p>
        The pattern: anything that prevents Google from reading the file is
        urgent. Anything that just means "we read it and it was empty" is
        not.
      </p>
      <p>
        If you're a Colorado business owner who'd rather have someone else
        manage this and a hundred other small SEO health-check items, that's
        what we do at Colorado Web Impressions. We monitor Search Console
        for our clients monthly and triage exactly these kinds of "is this
        a problem?" questions so you don't have to.
      </p>
    </LearnArticleLayout>
  );
}
